/**
 * Hover tooltip for the learner experience.
 * Wraps React Aria Tooltip with learner-themed styling: dark solid surface
 * (chosen over glass for legibility at small sizes), subtle arrow, and a
 * quick fade-and-drift animation in/out.
 *
 * Use via:
 *
 *   <TooltipTrigger delay={200} closeDelay={0}>
 *     <button>trigger</button>
 *     <Tooltip>content</Tooltip>
 *   </TooltipTrigger>
 *
 * The trigger must be a focusable element (button, link) — React Aria wires
 * up hover, focus, and touch long-press handlers for you.
 */
import {
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  OverlayArrow,
  type TooltipProps as AriaTooltipProps,
} from 'react-aria-components';

export interface TooltipProps extends Omit<AriaTooltipProps, 'className' | 'style' | 'children'> {
  className?: string;
  children?: React.ReactNode;
}

const ANIM_IN = 'fl-tooltip-in 150ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
const ANIM_OUT = 'fl-tooltip-out 100ms cubic-bezier(0.16, 1, 0.3, 1) forwards';

const KEYFRAMES = `
@keyframes fl-tooltip-in { from { opacity: 0; transform: translateY(-2px) } to { opacity: 1; transform: translateY(0) } }
@keyframes fl-tooltip-out { from { opacity: 1; transform: translateY(0) } to { opacity: 0; transform: translateY(-2px) } }
`;

export function Tooltip({ className, children, ...props }: TooltipProps) {
  return (
    <>
      <style>{KEYFRAMES}</style>
      <AriaTooltip
        offset={8}
        {...props}
        className={className}
        style={({ isEntering, isExiting }) => ({
          background: 'var(--color-text)',
          color: 'var(--color-text-on-accent)',
          padding: '8px 12px',
          borderRadius: 8,
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          lineHeight: 1.4,
          boxShadow: '0 6px 16px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.1)',
          animation: isEntering ? ANIM_IN : isExiting ? ANIM_OUT : undefined,
          zIndex: 50,
          maxWidth: 280,
        })}
      >
        {/* Arrow inherits background colour so it reads as part of the pill.
            React Aria sets data-placement on OverlayArrow; Tailwind arbitrary
            selectors rotate the SVG to point toward the trigger. */}
        <OverlayArrow>
          <svg
            width={10}
            height={10}
            viewBox="0 0 10 10"
            className="block data-[placement=bottom]:rotate-180 data-[placement=left]:-rotate-90 data-[placement=right]:rotate-90"
            style={{ fill: 'var(--color-text)' }}
          >
            <path d="M0 0 L5 5 L10 0" />
          </svg>
        </OverlayArrow>
        {children}
      </AriaTooltip>
    </>
  );
}

export { AriaTooltipTrigger as TooltipTrigger };
